import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { seoService } from '../../services/seoService';
import type { SEOSettings } from '../../types';

interface SEOTestResult {
  test: string;
  status: 'success' | 'error' | 'warning' | 'info';
  message: string;
  details?: string;
}

const SEOTestPage: React.FC = () => {
  const [seoSettings, setSeoSettings] = useState<SEOSettings | null>(null);
  const [testResults, setTestResults] = useState<SEOTestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  const fetchSEOSettings = async () => {
    try {
      setLoading(true);
      const settings = await seoService.getSEOSettings();
      setSeoSettings(settings);
      if (settings) {
        runSEOTests(settings);
      }
    } catch (error) {
      console.error('SEO ayarları yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const runSEOTests = (settings: SEOSettings) => {
    const results: SEOTestResult[] = [];

    // 1. Temel SEO Ayarları Testi
    if (!settings.siteName) {
      results.push({
        test: 'Site Adı',
        status: 'error',
        message: 'Site adı tanımlanmamış',
        details: 'SEO ayarlarından site adını tanımlayın'
      });
    } else {
      results.push({
        test: 'Site Adı',
        status: 'success',
        message: `Site adı: ${settings.siteName}`
      });
    }

    if (!settings.siteDescription) {
      results.push({
        test: 'Site Açıklaması',
        status: 'error',
        message: 'Site açıklaması tanımlanmamış',
        details: 'SEO ayarlarından site açıklamasını tanımlayın'
      });
    } else {
      results.push({
        test: 'Site Açıklaması',
        status: 'success',
        message: `Site açıklaması: ${settings.siteDescription.substring(0, 100)}...`
      });
    }

    if (!settings.defaultTitle) {
      results.push({
        test: 'Varsayılan Başlık',
        status: 'error',
        message: 'Varsayılan başlık tanımlanmamış',
        details: 'SEO ayarlarından varsayılan başlığı tanımlayın'
      });
    } else {
      results.push({
        test: 'Varsayılan Başlık',
        status: 'success',
        message: `Varsayılan başlık: ${settings.defaultTitle}`
      });
    }

    // 2. Open Graph Testi
    if (!settings.openGraph?.siteName) {
      results.push({
        test: 'Open Graph Site Adı',
        status: 'warning',
        message: 'Open Graph site adı tanımlanmamış',
        details: 'Sosyal medya paylaşımlarında site adı görünmeyecek'
      });
    } else {
      results.push({
        test: 'Open Graph Site Adı',
        status: 'success',
        message: `OG Site Adı: ${settings.openGraph.siteName}`
      });
    }

    if (!settings.openGraph?.defaultImage) {
      results.push({
        test: 'Open Graph Resmi',
        status: 'warning',
        message: 'Open Graph varsayılan resmi tanımlanmamış',
        details: 'Sosyal medya paylaşımlarında resim görünmeyecek'
      });
    } else {
      results.push({
        test: 'Open Graph Resmi',
        status: 'success',
        message: `OG Resim: ${settings.openGraph.defaultImage}`
      });
    }

    // 3. Twitter Cards Testi
    if (!settings.twitterCard?.cardType) {
      results.push({
        test: 'Twitter Card Tipi',
        status: 'warning',
        message: 'Twitter Card tipi tanımlanmamış',
        details: 'Twitter paylaşımlarında kart tipi belirlenmeyecek'
      });
    } else {
      results.push({
        test: 'Twitter Card Tipi',
        status: 'success',
        message: `Twitter Card: ${settings.twitterCard.cardType}`
      });
    }

    // 4. Analytics Testi
    if (!settings.analytics?.googleAnalyticsId) {
      results.push({
        test: 'Google Analytics',
        status: 'info',
        message: 'Google Analytics ID tanımlanmamış',
        details: 'Ziyaretçi analitikleri takip edilmeyecek'
      });
    } else {
      results.push({
        test: 'Google Analytics',
        status: 'success',
        message: `GA ID: ${settings.analytics.googleAnalyticsId}`
      });
    }

    if (!settings.analytics?.googleTagManagerId) {
      results.push({
        test: 'Google Tag Manager',
        status: 'info',
        message: 'Google Tag Manager ID tanımlanmamış',
        details: 'GTM ile tag yönetimi yapılamayacak'
      });
    } else {
      results.push({
        test: 'Google Tag Manager',
        status: 'success',
        message: `GTM ID: ${settings.analytics.googleTagManagerId}`
      });
    }

    // 5. Schema.org Testi
    if (!settings.schema?.enableOrganizationSchema) {
      results.push({
        test: 'Schema.org Organizasyon',
        status: 'info',
        message: 'Schema.org organizasyon şeması etkin değil',
        details: 'Arama motorları organizasyon bilgilerini göremeyecek'
      });
    } else {
      results.push({
        test: 'Schema.org Organizasyon',
        status: 'success',
        message: 'Organizasyon şeması etkin'
      });
    }

    // 6. Teknik SEO Testi
    if (!settings.technical?.enableRobotsTxt) {
      results.push({
        test: 'Robots.txt',
        status: 'warning',
        message: 'Robots.txt etkin değil',
        details: 'Arama motorları hangi sayfaları indeksleyeceğini bilemeyecek'
      });
    } else {
      results.push({
        test: 'Robots.txt',
        status: 'success',
        message: 'Robots.txt etkin'
      });
    }

    if (!settings.technical?.enableSitemap) {
      results.push({
        test: 'XML Sitemap',
        status: 'warning',
        message: 'XML Sitemap etkin değil',
        details: 'Arama motorları sitenizin yapısını anlayamayacak'
      });
    } else {
      results.push({
        test: 'XML Sitemap',
        status: 'success',
        message: 'XML Sitemap etkin'
      });
    }

    // 7. Sayfa Özel Ayarları Testi
    const pages = ['home', 'tours', 'blog', 'about', 'contact'] as const;
    pages.forEach(page => {
      const pageSettings = settings.pageSettings[page];
      if (!pageSettings?.title) {
        results.push({
          test: `${page.charAt(0).toUpperCase() + page.slice(1)} Sayfası Başlığı`,
          status: 'warning',
          message: `${page} sayfası için özel başlık tanımlanmamış`,
          details: 'Varsayılan başlık kullanılacak'
        });
      } else {
        results.push({
          test: `${page.charAt(0).toUpperCase() + page.slice(1)} Sayfası Başlığı`,
          status: 'success',
          message: `${page} sayfası başlığı tanımlı`
        });
      }
    });

    setTestResults(results);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'info':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const successCount = testResults.filter(r => r.status === 'success').length;
  const errorCount = testResults.filter(r => r.status === 'error').length;
  const warningCount = testResults.filter(r => r.status === 'warning').length;
  const infoCount = testResults.filter(r => r.status === 'info').length;

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          SEO Test Sonuçları
        </Typography>
        <Typography variant="body1" color="text.secondary">
          SEO ayarlarınızın durumunu kontrol edin ve eksiklikleri giderin.
        </Typography>
      </Box>

      {/* Özet Kartları */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">{successCount}</Typography>
            <Typography variant="body2">Başarılı</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="error.main">{errorCount}</Typography>
            <Typography variant="body2">Hata</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">{warningCount}</Typography>
            <Typography variant="body2">Uyarı</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">{infoCount}</Typography>
            <Typography variant="body2">Bilgi</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Test Sonuçları */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Test Sonuçları</Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                setTesting(true);
                setTimeout(() => {
                  if (seoSettings) runSEOTests(seoSettings);
                  setTesting(false);
                }, 1000);
              }}
              disabled={testing}
            >
              {testing ? 'Test Ediliyor...' : 'Yeniden Test Et'}
            </Button>
          </Box>

          <List>
            {testResults.map((result, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemIcon>
                    {getStatusIcon(result.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1">{result.test}</Typography>
                        <Chip
                          label={result.status.toUpperCase()}
                          size="small"
                          color={getStatusColor(result.status) as any}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {result.message}
                        </Typography>
                        {result.details && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {result.details}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < testResults.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Öneriler */}
      {errorCount > 0 && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Kritik SEO Sorunları Tespit Edildi
          </Typography>
          <Typography variant="body2">
            Yukarıdaki hataları düzeltmek için SEO Yönetimi sayfasına gidin ve gerekli ayarları yapın.
          </Typography>
        </Alert>
      )}

      {warningCount > 0 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            SEO İyileştirme Önerileri
          </Typography>
          <Typography variant="body2">
            Uyarı işaretli öğeleri düzeltmek SEO performansınızı artıracaktır.
          </Typography>
        </Alert>
      )}

      {successCount === testResults.length && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Mükemmel! Tüm SEO Ayarları Tamam
          </Typography>
          <Typography variant="body2">
            SEO ayarlarınız mükemmel durumda. Siteniz arama motorları için optimize edilmiş.
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default SEOTestPage; 